import type {
  Flag,
  FlagConfig,
  IdBasedFallthrough,
  IdBasedFlagConfig,
  IdBasedPrerequisite,
  IdBasedRollout,
  IdBasedRule,
  IdBasedTarget,
  Rollout,
  Variation,
} from './instructionTypes';

type MakeRequired<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

const isVariation = (variation: Variation | undefined): variation is MakeRequired<Variation, '_id'> =>
  variation !== undefined && variation._id !== undefined;

const ensureVariation = (variation: Variation | undefined): MakeRequired<Variation, '_id'> => {
  if (!isVariation(variation)) {
    throw new Error('Expected variation to have an _id');
  }
  return variation;
};

export function getControlAndTestVariations(params: {
  flagConfig: FlagConfig;
  variations: Variation[];
  fallthroughVariation: number | undefined;
  originalVariation: number | undefined;
  draftVariation: number | undefined;
}): { controlVariation: MakeRequired<Variation, '_id'>; testVariation: MakeRequired<Variation, '_id'> } {
  const { flagConfig, variations, originalVariation, draftVariation, fallthroughVariation } = params;
  const flagIsOff = !flagConfig.on;
  const offVariation = typeof flagConfig.offVariation === 'number' ? variations[flagConfig.offVariation] : undefined;

  let controlVariation = flagIsOff
    ? offVariation
    : typeof originalVariation === 'number'
      ? variations[originalVariation]
      : typeof fallthroughVariation === 'number'
        ? variations[fallthroughVariation]
        : offVariation;
  let testVariation = typeof draftVariation === 'number' ? variations[draftVariation] : undefined;

  if (!testVariation && !controlVariation) {
    testVariation = variations[0];
    controlVariation = variations[1];
  } else if (testVariation && !controlVariation) {
    // Note: compares string (_id) to Variation object — always true, so this
    // always returns variations[0]. Fixed in getIdBasedControlAndTestVariations.
    controlVariation = variations.find((variation) => variation._id !== testVariation);
  } else if (controlVariation && !testVariation) {
    testVariation = variations.find((variation) => variation._id !== controlVariation);
  }

  // If control and test variations are the same, we need to find a different test variation
  if (controlVariation?._id === testVariation?._id) {
    testVariation = variations.find((variation) => variation._id !== controlVariation?._id);
  }

  return { controlVariation: ensureVariation(controlVariation), testVariation: ensureVariation(testVariation) };
}

/**
 * Converts an index-based FlagConfig (the OpenAPI shape) into an IdBasedFlagConfig
 * by resolving every `variation: number` index to a `variationId: string`.
 */
export function convertFlagConfigToIdBased(
  config: FlagConfig,
  variations: Variation[],
  prerequisiteFlags: Flag[],
): IdBasedFlagConfig {
  const resolveId = (index: number): string => {
    const variation = variations[index];
    return variation?._id ?? String(index);
  };

  const convertPrerequisites = (prerequisites: FlagConfig['prerequisites']): IdBasedPrerequisite[] | undefined => {
    if (!prerequisites) {
      return undefined;
    }
    const prerequisiteVariationsById = Object.fromEntries(prerequisiteFlags.map((flag) => [flag.key, flag.variations]));
    return prerequisites.flatMap((p) => {
      if (!p.key) {
        return [];
      }
      // Fall back to '' when the prerequisite flag was deleted or isn't in prerequisiteFlags.
      // This is safe: unresolvable prerequisites only appear in the *original* config (you can't
      // add a prerequisite for a missing flag), so the diff produces removePrerequisite which
      // only needs the key, not the variationId.
      const variationId = prerequisiteVariationsById[p.key]?.[p.variation]?._id ?? '';
      return [{ key: p.key, variationId }];
    });
  };

  const convertTargets = (targets: FlagConfig['targets']): IdBasedTarget[] | undefined => {
    if (!targets) {
      return undefined;
    }
    return targets.map((t) => ({
      values: t.values,
      variationId: resolveId(t.variation),
    }));
  };

  const convertContextTargets = (contextTargets: FlagConfig['contextTargets']): IdBasedTarget[] | undefined => {
    if (!contextTargets) {
      return undefined;
    }
    return contextTargets.map((t) => ({
      values: t.values,
      variationId: resolveId(t.variation),
      contextKind: t.contextKind,
    }));
  };

  const convertRollout = (rollout: Rollout | undefined): IdBasedRollout | undefined => {
    if (!rollout) {
      return undefined;
    }
    return {
      variations: rollout.variations.map((wv) => ({
        variationId: resolveId(wv.variation),
        weight: wv.weight,
      })),
      contextKind: rollout.contextKind,
      bucketBy: rollout.bucketBy,
      experimentAllocation: rollout.experimentAllocation
        ? {
            canReshuffle: rollout.experimentAllocation.canReshuffle,
            defaultVariationId: resolveId(rollout.experimentAllocation.defaultVariation),
            type: rollout.experimentAllocation.type,
          }
        : undefined,
    };
  };

  const convertRules = (rules: FlagConfig['rules']): IdBasedRule[] | undefined => {
    if (!rules) {
      return undefined;
    }
    return rules.map((rule) => ({
      _id: rule._id,
      ref: rule.ref,
      description: rule.description,
      clauses: rule.clauses,
      variationId: typeof rule.variation === 'number' ? resolveId(rule.variation) : undefined,
      rollout: convertRollout(rule.rollout),
      trackEvents: rule.trackEvents,
      disabled: rule.disabled,
    }));
  };

  const convertFallthrough = (ft: FlagConfig['fallthrough']): IdBasedFallthrough | undefined => {
    if (!ft) {
      return undefined;
    }
    return {
      variationId: typeof ft.variation === 'number' ? resolveId(ft.variation) : undefined,
      rollout: convertRollout(ft.rollout),
    };
  };

  return {
    on: config.on,
    prerequisites: convertPrerequisites(config.prerequisites),
    targets: convertTargets(config.targets),
    contextTargets: convertContextTargets(config.contextTargets),
    rules: convertRules(config.rules),
    fallthrough: convertFallthrough(config.fallthrough),
    offVariationId: typeof config.offVariation === 'number' ? resolveId(config.offVariation) : undefined,
    // Passthrough fields
    archived: config.archived,
    _environmentName: config._environmentName,
    _site: config._site as { href: string },
    lastModified: config.lastModified,
    salt: config.salt,
    sel: config.sel,
    trackEvents: config.trackEvents,
    trackEventsFallthrough: config.trackEventsFallthrough,
    version: config.version,
  };
}

export function getIdBasedControlAndTestVariations(params: {
  flagConfig: IdBasedFlagConfig;
  variations: Variation[];
  fallthroughVariationId: string | undefined;
  originalVariationId: string | undefined;
  draftVariationId: string | undefined;
}): { controlVariation: MakeRequired<Variation, '_id'>; testVariation: MakeRequired<Variation, '_id'> } {
  const { flagConfig, variations, originalVariationId, draftVariationId, fallthroughVariationId } = params;
  const validVariations = variations.filter(isVariation);
  const variationById = new Map(validVariations.map((v) => [v._id, v]));
  const flagIsOff = !flagConfig.on;
  const offVariation = flagConfig.offVariationId ? variationById.get(flagConfig.offVariationId) : undefined;

  const findById = (id: string | undefined) => (id ? variationById.get(id) : undefined);

  let controlVariation = flagIsOff
    ? offVariation
    : (findById(originalVariationId) ?? findById(fallthroughVariationId) ?? offVariation);
  let testVariation = findById(draftVariationId);

  if (!testVariation && !controlVariation) {
    testVariation = validVariations[0];
    controlVariation = validVariations[1];
  } else if (testVariation && !controlVariation) {
    controlVariation = validVariations.find((variation) => variation._id !== testVariation?._id);
  } else if (controlVariation && !testVariation) {
    testVariation = validVariations.find((variation) => variation._id !== controlVariation?._id);
  }

  if (controlVariation?._id === testVariation?._id) {
    testVariation = validVariations.find((variation) => variation._id !== controlVariation?._id);
  }

  return { controlVariation: ensureVariation(controlVariation), testVariation: ensureVariation(testVariation) };
}
